import { useState } from 'react';

import { ApiError, api } from '../../api';
import { useLoad } from '../../data';
import { MoneyField, Panel, PageHead, StatusChip, fmtMoney } from '../../ui';
import { chosenMinor } from '../../price';
import { MONEY_FIELD } from '../../joining';
import { Connection, EvaAction, EvaGrant, EvaRule, GrantKey, Onboarding, Product } from '../../types';

// Eva's grant surface (canon 18): grants first, rules second, and "what Eva
// did" — the same page the supplier portal already reserved as "Eva rules".
// She has no authority until it is granted here, one capability at a time,
// each revocable in a tap. A grant is data; this screen is where the data is
// set, and the boundary is visible on it.

interface GrantMeta {
  key: GrantKey;
  title: string;
  what: string;
  writes?: boolean; // a grant that lets Eva act, not just read/compose
}

const GRANTS: GrantMeta[] = [
  { key: 'tell', title: 'Tell me things', what: 'Notify you, summarise, and answer questions about your own data. Read-only — this one stays on.' },
  { key: 'draft', title: 'Draft my replies', what: 'Write the words for a decline or a counter-offer. You send them — she never does.' },
  { key: 'money', title: 'Answer money questions', what: '“What did I earn in June.” The figures come from your ledger, never from a guess.' },
  { key: 'accept', title: 'Accept work that fits', what: 'Accept a request inside your availability that matches a rule you wrote. Stamped as hers, undoable for 30 minutes.', writes: true },
  { key: 'propose', title: 'Offer another time', what: 'Propose one of your own free slots when the requested time is taken. Holds the slot 12 hours; undoable for 30 minutes.', writes: true },
  { key: 'block', title: 'Block days for me', what: 'Block a day you’ve told her about — weather, maintenance — and tell the affected hotels.', writes: true },
];

export function Eva() {
  const grants = useLoad<{ grants: EvaGrant[] }>('/eva/grants');
  const rules = useLoad<{ rules: EvaRule[] }>('/eva/rules');
  const audit = useLoad<{ actions: EvaAction[] }>('/eva/audit');
  const products = useLoad<{ items: Product[] }>('/products');
  const connections = useLoad<{ items: Connection[] }>('/connections');
  const [busy, setBusy] = useState<string>('');
  const [error, setError] = useState('');

  const byKey = new Map((grants.data?.grants ?? []).map((g) => [g.key, g]));
  const accept = byKey.get('accept');

  const setGrant = async (key: GrantKey, on: boolean) => {
    setBusy(key);
    setError('');
    try {
      await api('PUT', `/eva/grants/${key}`, { on });
      await grants.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('');
    }
  };

  const undoAction = async (bookingId: string) => {
    try {
      await api('POST', `/bookings/${bookingId}/undo`);
    } catch {
      // The window may have closed under us — the reload shows the truth.
    }
    await Promise.all([audit.reload(), grants.reload()]);
  };

  return (
    <div>
      <PageHead
        title="Eva"
        sub="She starts with no authority. You give it one capability at a time, and take it back in a tap."
      />
      {error && <p className="error-line" role="alert">{error}</p>}

      <ChatPanel onActed={() => { audit.reload(); grants.reload(); }} />

      <Panel title="What Eva can do">
        <div className="eva-grants">
          {GRANTS.map((meta) => {
            const g = byKey.get(meta.key);
            // A rule-6-suspended grant reads as OFF (the domain contract): the
            // switch must never claim a capability that is accepting nothing,
            // and "Turn on" is what clears the suspension in one tap.
            const on = (g?.on ?? meta.key === 'tell') && !g?.suspended;
            const revocable = meta.key !== 'tell';
            return (
              <div key={meta.key} className={`eva-grant ${on ? 'on' : ''}`}>
                <div className="g-text">
                  <div className="g-title">
                    {meta.title}
                    {on && <span className="g-state">on</span>}
                  </div>
                  <div className="g-what">{meta.what}</div>
                  {g?.suspended && (
                    <div className="g-suspended">
                      Paused: {g.suspendedReason} — turn it back on when you’ve seen it.
                    </div>
                  )}
                </div>
                <button
                  className={`btn ${on ? 'secondary' : ''}`}
                  disabled={!revocable || busy === meta.key}
                  onClick={() => setGrant(meta.key, !on)}
                  title={!revocable ? 'Read-only — never revoked' : ''}
                >
                  {busy === meta.key ? '…' : on ? (revocable ? 'Turn off' : 'On') : 'Turn on'}
                </button>
              </div>
            );
          })}
          {/* Decline is shown, greyed, and never available (DECISION-026). */}
          <div className="eva-grant unavailable">
            <div className="g-text">
              <div className="g-title">Decline for me</div>
              <div className="g-what">
                Not yet. Eva will never decline work on your behalf until you can see a season of
                what she would have done.
              </div>
            </div>
            <button className="btn ghost" disabled>
              Not available
            </button>
          </div>
        </div>
      </Panel>

      {accept?.on && (
        <RulesPanel
          rules={rules.data?.rules ?? []}
          products={products.data?.items ?? []}
          connections={connections.data?.items ?? []}
          onChanged={rules.reload}
        />
      )}

      <Panel title="What Eva did" meta={`${audit.data?.actions.length ?? 0} actions`}>
        {audit.data && audit.data.actions.length === 0 ? (
          <div className="empty">
            <strong>Nothing yet</strong>
            <p>When Eva accepts, offers a time, or blocks a day, it lands here — with a tap back to the rule that let her, and undo while the window is open.</p>
          </div>
        ) : (
          <div className="eva-audit">
            {(audit.data?.actions ?? []).map((a, i) => (
              <div key={`${a.bookingId}-${i}`} className="a-row">
                <div className="a-main">
                  <span className="a-cap">{capabilityLabel(a.capability)}</span>
                  <span className="a-what">{a.productName ?? a.bookingId} · {a.hotelName ?? 'a hotel'}</span>
                  {a.via && <span className="a-why">because: {a.via}</span>}
                </div>
                <div className="a-side">
                  <StatusChip status={a.status} />
                  <span className="a-when">{new Date(a.at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}</span>
                  {a.canUndo && (
                    <button className="btn danger-outline" onClick={() => undoAction(a.bookingId)}>Undo</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// Where a reply's words came from (B17). Only `model` means Eva composed it;
// the rest is her deterministic core, and the desk says so for the same reason
// the phone does — a fallback that renders identically to a composed answer
// makes "Eva is working" unverifiable through the product.
type EvaSource = 'model' | 'deterministic' | 'budget' | 'error';
const SOURCE_NOTE: Partial<Record<EvaSource, string>> = {
  error: 'Answered offline',
  budget: "Today's limit reached",
};

// The desk's words for a message that did not go (B36) — said under the box,
// never as an Eva bubble (aegean is Eva's alone, canon 03).
const NOT_SENT = {
  noAnswer: 'No answer came back. Your message is still in the box — send it again, and Eva will act on it only once.',
  stillWorking: 'Eva is still answering this one — send it again in a moment.',
  refused: "Not sent — Eva couldn't take that message. Change it, then send it again.",
};

const GREETING =
  "I'm Eva. Ask me what's waiting, what you've earned, or which days you've blocked — and once you switch on “Accept work that fits”, I can take the requests that match your rules.";
// While setting up, or before that is known: no grant offered (DECISION-080 §5).
const GREETING_PLAIN = "I'm Eva. Ask me what's waiting, what you've earned, or which days you've blocked.";

function newId(prefix: string): string {
  const bytes = new Uint8Array(10);
  crypto.getRandomValues(bytes);
  return `${prefix}${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

function ChatPanel({ onActed }: { onActed?: () => void }) {
  const onboarding = useLoad<Onboarding>('/onboarding');
  const [messages, setMessages] = useState<{ from: 'user' | 'eva'; text: string; source?: EvaSource }[]>([]);
  // The thread id is made here, once, so a message sent again lands on the same
  // thread; each message's turn id lasts while its words are unchanged, so
  // sending them again is answered once (B36 review).
  const [threadId, setThreadId] = useState<string>(() => newId('th_'));
  const [turn, setTurn] = useState<{ text: string; id: string; for?: string } | null>(null);
  // The id of what Eva's last reply asked about (B36): sent with the next
  // message, so an answer typed under a question that has since moved on (a
  // second tab, the phone) saves nothing.
  const [shownFor, setShownFor] = useState<string | undefined>(undefined);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [notSent, setNotSent] = useState('');
  const greeting = onboarding.data?.status && onboarding.data.status !== 'onboarding' ? GREETING : GREETING_PLAIN;

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const again = !!turn && turn.text === text;
    const turnId = again ? turn!.id : newId('t_');
    const typedFor = again ? turn!.for : shownFor;
    setTurn({ text, id: turnId, ...(typedFor ? { for: typedFor } : {}) });
    setMessages((m) => [...m, { from: 'user', text }]);
    setInput('');
    setNotSent('');
    setSending(true);
    try {
      const r = await api<{
        threadId: string;
        text: string;
        source?: EvaSource;
        did?: { capability: string; bookingId: string }[];
        for?: string;
      }>('POST', '/eva/messages', { message: text, threadId, turnId, ...(typedFor ? { for: typedFor } : {}) });
      setThreadId(r.threadId);
      setTurn(null);
      setShownFor(r.for);
      setMessages((m) => [...m, { from: 'eva', text: r.text, source: r.source }]);
      // If Eva acted (an accept), refresh the audit + grants so "what Eva did"
      // and any rule-6 suspension show at once.
      if (r.did?.length) onActed?.();
    } catch (e) {
      // B36: what was typed is never lost. The message did not go — or its
      // answer never came — so it is not shown as said: it goes back in the box
      // (held while it was on its way, so nothing else is there), with the same
      // turn id, and the line under the box says what happened.
      const status = e instanceof ApiError ? e.status : 0;
      const code = e instanceof ApiError ? e.code : '';
      setMessages((m) => m.slice(0, -1));
      setInput(text);
      setNotSent(
        status === 409 && code === 'turn_in_progress'
          ? NOT_SENT.stillWorking
          : status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429
            ? NOT_SENT.refused
            : NOT_SENT.noAnswer,
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Panel title="Ask Eva">
      <div className="eva-chat-desk" role="log" aria-live="polite">
        <div className="d-msg eva">
          <div>{greeting}</div>
        </div>
        {messages.map((m, i) => (
          <div key={i} className={`d-msg ${m.from}`}>
            {m.text.split('\n').map((line, j) => (
              <div key={j}>{line}</div>
            ))}
            {m.source && SOURCE_NOTE[m.source] && (
              <div className="d-msg-source">{SOURCE_NOTE[m.source]}</div>
            )}
          </div>
        ))}
        {sending && <div className="d-msg eva">…</div>}
      </div>
      <div className="row" style={{ marginTop: 'var(--space-md)' }}>
        <input
          style={{ flex: 1 }}
          value={input}
          disabled={sending}
          maxLength={2000}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder="Ask Eva…"
          aria-label="Message to Eva"
        />
        <button className="btn" onClick={send} disabled={sending || !input.trim()} style={{ flex: 'none' }}>Send</button>
      </div>
      {notSent && <p className="error-line" role="status">{notSent}</p>}
    </Panel>
  );
}

function capabilityLabel(cap: string): string {
  if (cap === 'auto-accept') return 'Accepted';
  if (cap === 'escalation') return 'Escalated';
  return cap;
}

function RulesPanel({
  rules,
  products,
  connections,
  onChanged,
}: {
  rules: EvaRule[];
  products: Product[];
  connections: Connection[];
  onChanged: () => Promise<void>;
}) {
  const [adding, setAdding] = useState(false);
  const hotels = connections
    .filter((c) => c.status === 'active')
    .map((c) => ({ id: c.hotelAccountId ?? c.accountId ?? '', name: c.hotelName ?? c.name ?? 'A hotel' }))
    .filter((h) => h.id);

  const [delError, setDelError] = useState('');
  const del = async (id: string) => {
    setDelError('');
    try {
      await api('DELETE', `/eva/rules/${id}`);
      await onChanged();
    } catch (e) {
      setDelError((e as Error).message);
    }
  };

  return (
    <Panel
      title="What she may accept"
      meta={!adding ? <button className="btn secondary" onClick={() => setAdding(true)}>Add a rule</button> : undefined}
    >
      <p className="sub" style={{ marginTop: 0 }}>
        Eva accepts a request only if it matches one of these and the day is free. A blank field
        means “any”. The narrower rule wins.
      </p>
      {rules.length === 0 && !adding && (
        <p className="hint">No rules yet — so she accepts nothing. Add one to say what “work that fits” means.</p>
      )}
      <div className="eva-rules">
        {rules.map((r) => (
          <div key={r.id} className="rule-row">
            <div className="r-text">
              <div className="r-title">{r.label || ruleSentence(r, products, hotels)}</div>
              {r.label && <div className="r-detail">{ruleSentence(r, products, hotels)}</div>}
            </div>
            <button className="btn danger-outline" onClick={() => del(r.id)}>Remove</button>
          </div>
        ))}
      </div>
      {delError && <p className="error-line" role="alert">{delError}</p>}
      {adding && (
        <RuleForm
          products={products}
          hotels={hotels}
          onCancel={() => setAdding(false)}
          onSaved={async () => { setAdding(false); await onChanged(); }}
        />
      )}
    </Panel>
  );
}

function ruleSentence(r: EvaRule, products: Product[], hotels: { id: string; name: string }[]): string {
  const parts: string[] = [];
  if (r.products?.length) {
    parts.push(r.products.map((p) => products.find((x) => x.id === p)?.name ?? p).join(', '));
  } else {
    parts.push('Any product');
  }
  if (r.hotels?.length) parts.push(`from ${r.hotels.map((h) => hotels.find((x) => x.id === h)?.name ?? 'a hotel').join(', ')}`);
  if (typeof r.maxGuests === 'number') parts.push(`up to ${r.maxGuests} guests`);
  if (typeof r.maxValueMinor === 'number') parts.push(`up to ${fmtMoney({ minor: r.maxValueMinor, currency: 'EUR' })}`);
  if (r.dateFrom || r.dateTo) parts.push(`${r.dateFrom ?? 'any date'} to ${r.dateTo ?? 'open'}`);
  return parts.join(' · ');
}

function RuleForm({
  products,
  hotels,
  onCancel,
  onSaved,
}: {
  products: Product[];
  hotels: { id: string; name: string }[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [label, setLabel] = useState('');
  const [product, setProduct] = useState('');
  const [pickedHotels, setPickedHotels] = useState<string[]>([]);
  const [maxGuests, setMaxGuests] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [maxValueChoice, setMaxValueChoice] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setBusy(true);
    setError('');
    const body: Record<string, unknown> = {};
    if (label.trim()) body.label = label.trim();
    if (product) body.products = [product];
    if (pickedHotels.length) body.hotels = pickedHotels;
    if (maxGuests) body.maxGuests = Number(maxGuests);
    // N39 review: read through price.ts (a comma is the decimal point; "1.200"
    // is asked) — `Number("12,50")` was NaN and went as null. Unreadable or still
    // to choose: not sent, and said.
    if (maxValue.trim()) {
      const minor = chosenMinor(maxValue, maxValueChoice);
      if (minor === undefined) {
        setError(MONEY_FIELD.bad);
        setBusy(false);
        return;
      }
      body.maxValueMinor = minor;
    }
    if (dateFrom) body.dateFrom = dateFrom;
    if (dateTo) body.dateTo = dateTo;
    try {
      await api('POST', '/eva/rules', body);
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <div className="rule-form">
      <div className="field">
        <label>Name it (optional)</label>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Island tours up to 6 guests" />
      </div>
      <div className="field">
        <label>Product</label>
        <select value={product} onChange={(e) => setProduct(e.target.value)}>
          <option value="">Any product</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>
      {hotels.length > 0 && (
        <div className="field">
          <label>Hotels (blank = any)</label>
          <div className="chips-pick">
            {hotels.map((h) => (
              <button
                key={h.id}
                type="button"
                className={`pick ${pickedHotels.includes(h.id) ? 'selected' : ''}`}
                onClick={() =>
                  setPickedHotels((cur) => (cur.includes(h.id) ? cur.filter((x) => x !== h.id) : [...cur, h.id]))
                }
              >
                {h.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="row">
        <div className="field" style={{ flex: 1 }}>
          <label>Up to how many guests</label>
          <input className="mono" type="number" min={1} value={maxGuests} onChange={(e) => setMaxGuests(e.target.value)} placeholder="any" />
        </div>
        <MoneyField
          id="rule-max-value"
          label="Up to how much (€)"
          style={{ flex: 1 }}
          placeholder="any"
          value={maxValue}
          choice={maxValueChoice}
          onChange={(v) => {
            setMaxValue(v);
            setMaxValueChoice(null);
          }}
          onChoose={setMaxValueChoice}
        />
      </div>
      <div className="row">
        <div className="field" style={{ flex: 1 }}>
          <label>From</label>
          <input className="mono" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>To</label>
          <input className="mono" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>
      {error && <p className="error-line" role="alert">{error}</p>}
      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button className="btn secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save rule'}</button>
      </div>
    </div>
  );
}
